// components
// import { Button } from '@/components/atoms/Button/Button';
import { Card } from '@/components/atoms/Card/Card';
import { Chip } from '@/components/atoms/Chip/Chip';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Image } from '@/components/atoms/Image/Image';
import { NavLink } from '@/components/atoms/NavLink/NavLink';
import { Summary } from '@/components/molecules/CareProviderSummary/Summary';
import { ShortlistProgress } from '@/components/organisms/ShortlistProgress/ShortlistProgress';

// fixtures
import { shortlistItems } from '@/components/organisms/ShortlistProgress/fixtures';

// models
import { ProviderType, CareServiceType } from '@karehero/models';

// assets
// @transform-path ../../../../../../src/assets/app/header.svg
import defaultHeader from '@/assets/app/header.svg';

import {
  FeatureBullets,
  FeatureBulletsItem,
  CardBody,
  CardBody2,
  CardDetails,
  CardHeader,
  CardActions,
  ServiceName,
  ServiceAddress,
  ServiceTypeChips,
  GuidanceLabel,
  AvailabilityChipContainer,
  NoteContent,
  ExpertNoteContainer,
  NoteHeader,
  NoteTitle,
  CardFooter,
  CardHeaderWrapper,
} from './parts';
import React, { useContext } from 'react';
import Button from '@/components/atoms/Button/Button';
import { caresourcerHomeUrl } from '@/directory/constants';
import { DirectoryInteractionsContext } from '@/contexts/DirectoryInteractions';

const careTypeChips: { [key: string]: any } = {
  careHome: 'Care Home',
  homeCare: 'Home Care Agency',
};

const availabilityLabels: { [key: string]: any } = {
  careHome: 'Rooms available',
  homeCare: 'Accepting clients',
};

const singleLineFeatures = (provider?: ProviderType) => {
  if (!provider) return null;

  const {
    bedCount,
    dateOpened,
    purposeBuilt,
    doubleBedCount,
    enSuiteRoomCount,
  } = provider;

  const featuresText = [
    bedCount ? `${bedCount} beds` : null,
    enSuiteRoomCount && enSuiteRoomCount === bedCount ? `All en-suite` : null,
    enSuiteRoomCount && enSuiteRoomCount !== bedCount
      ? `${enSuiteRoomCount} en-suite rooms`
      : null,
    dateOpened ? `Opened in ${dateOpened.split('-')[0]}` : null,
  ].filter((x) => x);

  const featuresItems = featuresText.map((x, i) => (
    <React.Fragment key={x}>
      <FeatureBulletsItem>{x}</FeatureBulletsItem>
      {i !== featuresText.length - 1 && (
        <Icon size='2xs' type='fas' icon='circle' />
      )}
    </React.Fragment>
  ));

  return <FeatureBullets>{featuresItems}</FeatureBullets>;
};

function slugify(str: string) {
  str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
  str = str.toLowerCase(); // convert string to lowercase
  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-'); // remove consecutive hyphens
  return str;
}

export const ShortlistedCareHomeCard = ({
  regulatoryID = '',
  headerImageUrl = '',
  name = '',
  address = '',
  rating = '',
  phone = '',
  notes = '',
  careServiceType = [],
  available = false,
  isShowShortlistDetails = true,
  isSecured = false,
  careType,
  pricePerWeek,
  distance,
  minimumVisitTime,
  hourlyRate,
  onMarkSecured,
  provider,
  variant,
}: ShortlistedCareHomeCardProps) => {
  const { isShowResultsInApp } = useContext(DirectoryInteractionsContext);

  const availabilityLabel = availabilityLabels[careType] || 'Rooms available';
  const providerLink = isShowResultsInApp
    ? `/directory/providers/${regulatoryID}`
    : `${caresourcerHomeUrl}/uk/providers/${provider?.regulator || 'cqc'}/${regulatoryID}/${slugify(name)}`.toLowerCase();

  return (
    <Card
      isNoPadding
      to={providerLink}
      topChip={[careTypeChips[careType], provider?.address?.city]
        .filter((x) => !!x)
        .join(' - ')}
      elevation={0}
      isBordered={false}
      color='none'
      header={
        <CardHeaderWrapper $isRow={variant === 'long' && true}>
          <CardHeader>
            {headerImageUrl ? (
              <img src={headerImageUrl} alt='Service Header' />
            ) : (
              <Image src={defaultHeader} alt='Default Header' />
            )}
            {available && (
              <AvailabilityChipContainer>
                <Chip key='availability' label={availabilityLabel} />
              </AvailabilityChipContainer>
            )}
          </CardHeader>
          <CardBody isNoPadding>
            <CardDetails>
              <ServiceName>{name}</ServiceName>
              <ServiceAddress>{address}</ServiceAddress>
              <ServiceTypeChips>
                {careServiceType
                  .filter((service) => !!service.name)
                  .map((service) => (
                    <Chip key={service.id} label={service.name || ''} />
                  ))}
              </ServiceTypeChips>
              <Summary
                isSmall
                summaryItems={{
                  rating,
                  distance,
                  minimumVisitTime,
                  hourlyRate,
                }}
                careType={careType || ''}
              />
            </CardDetails>
          </CardBody>
        </CardHeaderWrapper>
      }
      footer={
        <CardFooter $isMobile={variant !== 'long'}>
          {phone && (
            <NavLink
              href={`tel:${phone.replace(/\s/g, '')}`}
              leftIcon='phone'
              leftIconType='fal'
              label={phone}
              size={'md'}
              isNoUnderline
              isNoChevron
              isButton
            />
          )}
          <NavLink
            href={providerLink}
            size={'md'}
            label={
              pricePerWeek
                ? `From £${pricePerWeek} / week`
                : provider?.hourlyRate
                  ? `From £${provider.hourlyRate.toFixed(2)} / hour`
                  : 'See more'
            }
          />
          {isShowShortlistDetails && (
            <>
              <Button
                ariaLabel={isSecured ? 'Service Secured' : 'Mark as Secured'}
                onPress={onMarkSecured}
                isFullWidth
                isPreventDefault
              >
                {isSecured ? 'Secured' : 'I Have Secured This Service'}
              </Button>
              <GuidanceLabel>
                <Icon icon='info-circle' fontSize={14} /> Confirm to receive
                guidance on managing care effectively
              </GuidanceLabel>
            </>
          )}
        </CardFooter>
      }
    >
      {variant === 'long' && provider?.description && (
        <CardBody2>
          {provider && singleLineFeatures(provider)}
          <ServiceAddress>
            {provider.description.length >= 500
              ? provider.description.substring(0, 500) + '...'
              : provider.description}
          </ServiceAddress>
        </CardBody2>
      )}
      {isShowShortlistDetails && (
        <CardBody2>
          <ShortlistProgress shortlistItems={shortlistItems} />
          <CardActions>
            <ExpertNoteContainer>
              <NoteHeader>
                <Icon icon='edit' size='lg' fontSize={14} />
                <NoteTitle>Care Expert Note</NoteTitle>
              </NoteHeader>
              <NoteContent>{notes || 'No Note Available'}</NoteContent>
            </ExpertNoteContainer>
          </CardActions>
        </CardBody2>
      )}
    </Card>
  );
};

export interface ShortlistedCareHomeCardProps {
  regulatoryID?: string;
  headerImageUrl?: string;
  name?: string;
  address?: string;
  rating?: string;
  phone?: string;
  careServiceType?: CareServiceType[];
  available?: boolean;
  isSecured?: boolean;
  isShowShortlistDetails?: boolean;
  notes?: string;
  onMarkSecured?: () => void;
  careType?: any;
  pricePerWeek?: any;
  distance?: any;
  minimumVisitTime?: any;
  hourlyRate?: any;
  provider?: ProviderType;
  variant?: 'shortlist' | 'search' | 'long';
}

ShortlistedCareHomeCard.displayName = 'ShortlistedCareHomeCard';

export default ShortlistedCareHomeCard;
